import Url from 'url';

import * as amplitude from '@amplitude/analytics-browser';
import { find, flatMapDeep, replace } from 'lodash';

export const generateUrls = (items, origin) =>
  flatMapDeep(items, ({ subItems, url }) => [
    replace(url, origin, ''),
    generateUrls(subItems, origin),
  ]);

export const getBestMatch = (items, origin, path, query) => {
  const urls = generateUrls(items, origin);
  const pathAndQuery = [path, query].join('');

  return (
    find(urls, (url) => pathAndQuery === url) ||
    find(urls, (url) => path === url)
  );
};

const generateWhiteLabelUrl = (item, getPageUrl) => {
  if (item.external) {
    return item.url;
  }

  if (getPageUrl) {
    return getPageUrl(item.url);
  }

  return item.url;
};

const prepareItem = (item, getPageUrl) => ({
  isCustomPage: item.is_custom_page,
  exact: item.url === '/',
  url: generateWhiteLabelUrl(item, getPageUrl),
  title: item.menu_name,
  external: item.external,
  openInNewTab: item.open_in_new_tab,
});

export const prepareItems = (appPages, getPageUrl) =>
  appPages.reduce((result, { menu, submenus }) => {
    if (menu.display_in_menu) {
      result.push({
        ...prepareItem(menu, getPageUrl),
        subItems: submenus?.reduce((result, subMenu) => {
          if (subMenu.display_in_menu) {
            result.push({
              ...prepareItem(subMenu, getPageUrl),
            });
          }

          return result;
        }, []),
      });
    }

    return result;
  }, []);

export const prepareItemsV2 = (appPages, getPageUrl) =>
  appPages.reduce((result, { menu, submenus }) => {
    result.push({
      ...prepareItem(menu, getPageUrl),
      subItems: submenus?.reduce((result, subMenu) => {
        result.push({
          ...prepareItem(subMenu, getPageUrl),
        });

        return result;
      }, []),
    });

    return result;
  }, []);

export const getMenuItemElements = (
  parentElement,
  dataItemType = 'menu-item',
) =>
  Array.from(
    parentElement.querySelectorAll(`a[data-item-type="${dataItemType}"]`),
  );

export const isExternal = ({ item, currentUrl }) => {
  const isSamePathname =
    item.url.startsWith(currentUrl.origin) &&
    Url.parse(item.url).pathname === window?.location?.pathname;

  return (
    (item.external &&
      !item.url.startsWith('/') &&
      !item.url.startsWith(currentUrl.origin)) ||
    isSamePathname ||
    item.target
  );
};

export const extendedOnItemsClick = ({
  currentUrl,
  isSubTab,
  item,
  onItemClick,
  shouldLogAmplitudeEvents,
}) => {
  if (shouldLogAmplitudeEvents) {
    let tabType;

    if (isExternal({ item, currentUrl })) {
      tabType = 'externalLink';
    } else if (item.url.startsWith(currentUrl.origin)) {
      tabType = 'internalLink';
    } else if (item.isCustomPage === true) {
      tabType = 'customPage';
    } else {
      tabType = 'nativeTab';
    }

    amplitude.track('Navigation|NavigatedToTab', {
      isSubTab,
      path: item.url.startsWith(currentUrl.origin)
        ? item.url.replace(currentUrl.origin, '')
        : item.url,
      tabType,
    });
  }

  return item.openInNewTab ? undefined : onItemClick;
};

export const getLocationState = ({ isLocal, path }) => {
  if (isLocal) {
    const { search } = Url.parse(path);

    return { fromMenu: true, search };
  }

  return null;
};

export const setFocusAtMenuItem = (parentNode, inputId) => {
  const button = parentNode?.querySelector(
    `:scope [aria-controls="${inputId}-sub-menu"]`,
  );
  const item = parentNode?.querySelector(':scope [role="menuitem"]');

  (button || item)?.focus();
};

export const setFocusAtSubMenuItem = (parentNode) => {
  // Select first sub-menu item
  parentNode?.querySelector(':scope [role="menu"] [role="menuitem"]')?.focus();
};
